import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Package, ChevronRight, Plus, Mail, Truck, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { getOrders, getUnreadCountsForOrders, Order } from '../lib/purchaseOrders';
import { getStockItems, StockItem } from '../lib/stockItems';
import {
  CustomerOrder,
  fetchCustomerOrders,
  orderStatusLabel,
} from '../lib/customerOrders';
import CreateOrderModal from './CreateOrderModal';
import PlaceOrderModal from './PlaceOrderModal';
import PurchaseOrderDetailModal from './PurchaseOrderDetailModal';
import { OrderDetailModal, NewOrderModal } from './orders/CustomerOrdersTab';
import { OrderRow } from './orders/OrderRow';

type WidgetTab = 'orders' | 'pos';

/** Same rule as the red Low badge on an inventory product card. */
async function countLowStockItems(businessId: string): Promise<number> {
  if (!supabase) return 0;
  const { data, error } = await supabase
    .from('stock_items')
    .select('quantity, reorder_point, track_inventory')
    .eq('business_id', businessId)
    .eq('is_deleted', false);
  if (error) throw error;
  return (data || []).filter((item) =>
    item.track_inventory !== false
    && item.reorder_point != null
    && item.quantity <= item.reorder_point
  ).length;
}

const TAB_STORAGE_KEY = 'pinned_orders_tab';

export const OrdersDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateOrderOpen, setIsCreateOrderOpen] = useState(false);
  const [isPlaceOrderOpen, setIsPlaceOrderOpen] = useState(false);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  // Unread supplier email replies per order id (matched via order_email_threads).
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  // Which views the business pinned (settings): POs, customer orders, or both.
  const [showPos, setShowPos] = useState(true);
  const [showOrders, setShowOrders] = useState(false);
  const [tab, setTab] = useState<WidgetTab>(() =>
    localStorage.getItem(TAB_STORAGE_KEY) === 'orders' ? 'orders' : 'pos'
  );
  const [custOrders, setCustOrders] = useState<CustomerOrder[]>([]);
  const [selectedCustOrder, setSelectedCustOrder] = useState<CustomerOrder | null>(null);
  const [isNewCustOrderOpen, setIsNewCustOrderOpen] = useState(false);
  const [custFilter, setCustFilter] = useState<'active' | 'all' | CustomerOrder['status']>('active');
  const [lowStockCount, setLowStockCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // The mobile panel is portaled to document.body, so it lives OUTSIDE
  // dropdownRef — the outside-click check must consider it "inside".
  const portalContentRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(null);
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();

  // Load the content settings and follow live changes from the settings modal.
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;
    let cancelled = false;
    (async () => {
      try {
        const [posRes, ordersRes] = await Promise.all([
          supabase.rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'inventory',
            p_setting_key: 'pin_orders_show_pos',
            p_default_value: true,
          }),
          supabase.rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'inventory',
            p_setting_key: 'pin_orders_show_orders',
            p_default_value: false,
          }),
        ]);
        if (cancelled) return;
        if (!posRes.error && posRes.data !== null) setShowPos(posRes.data);
        if (!ordersRes.error && ordersRes.data !== null) setShowOrders(ordersRes.data);
      } catch (err) {
        console.warn('Failed to load pinned orders content settings:', err);
      }
    })();

    const handleSettingChange = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.key === 'pin_orders_show_pos') setShowPos(detail.value);
      if (detail?.key === 'pin_orders_show_orders') setShowOrders(detail.value);
    };
    window.addEventListener('inventorySettingChanged', handleSettingChange);
    return () => {
      cancelled = true;
      window.removeEventListener('inventorySettingChanged', handleSettingChange);
    };
  }, [selectedBusiness?.id]);

  const loadOrders = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    setIsLoading(true);
    try {
      if (showPos) {
        const [data, lowCount] = await Promise.all([
          getOrders(selectedBusiness.id),
          countLowStockItems(selectedBusiness.id),
        ]);
        setOrders(data);
        setLowStockCount(lowCount);
        // Keep an open detail modal in sync after receives/edits.
        setSelectedOrder((prev) => (prev ? data.find((o) => o.id === prev.id) ?? null : null));

        // Unread supplier replies for the open orders (single batched fetch).
        const pendingIds = data.filter((o) => o.status === 'pending' && o.id).map((o) => o.id as string);
        setUnreadCounts(await getUnreadCountsForOrders(selectedBusiness.id, pendingIds));
      } else {
        setLowStockCount(0);
      }
      if (showOrders) {
        // Fetch everything; the status pills filter client-side.
        setCustOrders(await fetchCustomerOrders(selectedBusiness.id, { status: 'all' }));
      }
    } catch (err) {
      console.error('Error loading orders:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBusiness?.id, showPos, showOrders]);

  // Load on mount so the anchor badges (pending count + unread replies) are
  // visible without opening the dropdown first.
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Live badge updates: new matched supplier replies + read-status changes
  // (and tab focus as a safety net if the socket was briefly disconnected).
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefresh = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        void loadOrders();
      }, 400);
    };

    const channel = supabase
      .channel(`orders_widget_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'order_email_threads',
          filter: `business_id=eq.${selectedBusiness.id}`,
        },
        (payload) => {
          const row = payload.new as { direction?: string } | null;
          if (row?.direction === 'inbound') scheduleRefresh();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'all_messages',
          filter: `business_id=eq.${selectedBusiness.id}`,
        },
        (payload) => {
          const next = payload.new as { message_type?: string; read?: string } | null;
          const prev = payload.old as { read?: string } | null;
          // Only re-fetch when an email's read flag actually changes (badge clear).
          if (next?.message_type === 'email' && prev?.read !== next?.read) {
            scheduleRefresh();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'purchase_orders',
          filter: `business_id=eq.${selectedBusiness.id}`,
        },
        () => scheduleRefresh()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'customer_orders',
          filter: `business_id=eq.${selectedBusiness.id}`,
        },
        () => scheduleRefresh()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stock_items',
          filter: `business_id=eq.${selectedBusiness.id}`,
        },
        () => scheduleRefresh()
      )
      .subscribe();

    const onVisible = () => {
      if (document.visibilityState === 'visible') scheduleRefresh();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      document.removeEventListener('visibilitychange', onVisible);
      void supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id, loadOrders]);

  useEffect(() => {
    if (isOpen) {
      loadOrders();

      // Always portal to document.body with fixed positioning so page-level
      // headers (e.g. CalendarV2's) can never stack above the dropdown.
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          right: window.innerWidth < 640
            ? Math.min(16, window.innerWidth - rect.right)
            : Math.max(16, window.innerWidth - rect.right),
        });
      }
    }
  }, [isOpen, loadOrders]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideAnchor = dropdownRef.current?.contains(target) ?? false;
      const insidePortal = portalContentRef.current?.contains(target) ?? false;
      if (!insideAnchor && !insidePortal) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const pendingOrders = orders.filter(o => o.status === 'pending');
  const pendingCount = pendingOrders.length;
  const totalUnreadReplies = Object.values(unreadCounts).reduce((sum, c) => sum + c, 0);

  // Customer orders needing attention = still in "new" (nobody has confirmed).
  const newCustCount = custOrders.filter(o => o.status === 'new').length;
  // Active = not yet delivered/cancelled — the badge always shows this count.
  const activeCustCount = custOrders.filter(o =>
    ['new', 'confirmed', 'out_for_delivery'].includes(o.status)
  ).length;
  // Anchor badge: all active orders (+ pending POs when that view is pinned).
  // Red = something in here still needs confirming; neutral otherwise.
  const badgeCount = (showPos ? pendingCount : 0) + (showOrders ? activeCustCount : 0);
  const badgeUrgent = (showOrders && newCustCount > 0) || (showPos && pendingCount > 0);

  const bothViews = showPos && showOrders;
  // Effective tab: forced when only one view is enabled.
  const activeTab: WidgetTab = bothViews ? tab : showOrders ? 'orders' : 'pos';

  const switchTab = (next: WidgetTab) => {
    setTab(next);
    localStorage.setItem(TAB_STORAGE_KEY, next);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    // date-only columns (yyyy-mm-dd) parse as local calendar day
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr.slice(0, 10)) && dateStr.length === 10) {
      const [y, m, d] = dateStr.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('en-US', opts);
    }
    return new Date(dateStr).toLocaleDateString('en-US', opts);
  };

  const formatMoney = (amount: number) =>
    `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleOrderClick = (order: Order) => {
    setIsOpen(false);
    setSelectedOrder(order);
  };

  const handleViewAll = () => {
    setIsOpen(false);
    navigate(activeTab === 'orders' ? '/stock?view=customer-orders' : '/stock?view=orders');
  };

  const handleCreateOrder = async () => {
    if (activeTab === 'orders') {
      setIsOpen(false);
      setIsNewCustOrderOpen(true);
      return;
    }
    if (selectedBusiness?.id) {
      try {
        const items = await getStockItems(selectedBusiness.id);
        setStockItems(items);
      } catch (err) {
        console.error('Error loading stock items:', err);
      }
    }
    setIsOpen(false);
    setIsCreateOrderOpen(true);
  };

  const handleLowInventory = async () => {
    if (selectedBusiness?.id) {
      try {
        const items = await getStockItems(selectedBusiness.id);
        setStockItems(items);
      } catch (err) {
        console.error('Error loading stock items:', err);
      }
    }
    setIsOpen(false);
    setIsPlaceOrderOpen(true);
  };

  const handleCustOrderClick = (order: CustomerOrder) => {
    setIsOpen(false);
    setSelectedCustOrder(order);
  };

  // All open POs (legacy 'pending' = sent + partially received); list scrolls.
  const displayOrders = pendingOrders;
  const filteredCustOrders =
    custFilter === 'all'
      ? custOrders
      : custFilter === 'active'
      // Active = goods not yet delivered (never cancelled).
      ? custOrders.filter(o => o.status !== 'cancelled' && o.status !== 'delivered')
      : custOrders.filter(o => o.status === custFilter);
  const activeList = activeTab === 'orders' ? filteredCustOrders : displayOrders;

  const CUST_FILTERS: Array<{ id: 'active' | 'all' | CustomerOrder['status']; label: string }> = [
    { id: 'active', label: 'Active' },
    { id: 'new', label: 'New' },
    { id: 'confirmed', label: 'Confirmed' },
    { id: 'out_for_delivery', label: 'Out / Ready' },
    { id: 'delivered', label: 'Delivered' },
    { id: 'cancelled', label: 'Cancelled' },
    { id: 'all', label: 'All' },
  ];

  const dropdownContent = (
    <>
      <div className="px-5 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {bothViews ? 'Orders' : activeTab === 'orders' ? 'Orders' : 'Purchase Orders'}
          </h3>
          <button
            onClick={handleCreateOrder}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:scale-95 transition-all"
          >
            <Plus size={14} />
            Order
          </button>
        </div>
        {bothViews && (
          <div className="mt-3 grid grid-cols-2 gap-1 bg-gray-100 rounded-xl p-1">
            {([
              { id: 'orders' as WidgetTab, label: 'Orders', count: newCustCount },
              { id: 'pos' as WidgetTab, label: 'POs', count: pendingCount },
            ]).map(t => (
              <button
                key={t.id}
                onClick={() => switchTab(t.id)}
                className={`flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[13px] font-semibold transition-colors ${
                  activeTab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
                {t.count > 0 && (
                  <span className={`min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full text-[10px] font-bold ${
                    activeTab === t.id ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-700'
                  }`}>
                    {t.count > 9 ? '9+' : t.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        {activeTab === 'orders' && (
          <div className="mt-3 flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5">
            {CUST_FILTERS.map(f => (
              <button
                key={f.id}
                onClick={() => setCustFilter(f.id)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap transition-colors ${
                  custFilter === f.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="max-h-[500px] overflow-y-auto">
        {isLoading && (
          <div className="py-16 px-6 text-center">
            <div className="w-6 h-6 mx-auto mb-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-gray-500">Loading orders...</p>
          </div>
        )}

        {!isLoading && activeTab === 'orders' && filteredCustOrders.length === 0 && (
          <div className="py-16 px-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Truck size={24} className="text-gray-400" />
            </div>
            <p className="text-base font-medium text-gray-900 mb-1">
              {custFilter === 'all'
                ? 'No Orders Yet'
                : custFilter === 'active'
                ? 'No Active Orders'
                : `No ${orderStatusLabel(custFilter).toLowerCase()} orders`}
            </p>
            <p className="text-sm text-gray-500">Customer orders will appear here</p>
          </div>
        )}

        {!isLoading && activeTab === 'orders' && filteredCustOrders.length > 0 && (
          <div className="p-3 space-y-2">
            {filteredCustOrders.map((order) => (
              <OrderRow key={order.id} order={order} onClick={() => handleCustOrderClick(order)} />
            ))}
          </div>
        )}

        {!isLoading && activeTab === 'pos' && displayOrders.length === 0 && (
          <div className="py-16 px-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Package size={24} className="text-gray-400" />
            </div>
            <p className="text-base font-medium text-gray-900 mb-1">No Pending Orders</p>
            <p className="text-sm text-gray-500">All orders have been received</p>
          </div>
        )}

        {!isLoading && activeTab === 'pos' && displayOrders.length > 0 && (
          <div>
            {lowStockCount > 0 && (
              <div className="px-3 pt-3 pb-1">
                <button
                  type="button"
                  onClick={handleLowInventory}
                  className="flex w-full items-center gap-2 rounded-xl bg-red-500 px-3 py-2 text-left text-white shadow-sm hover:bg-red-600 active:bg-red-700 transition-colors touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-[13px] font-semibold">Low inventory</span>
                  <span className="ml-auto text-[13px] font-bold tabular-nums">
                    {lowStockCount} item{lowStockCount === 1 ? '' : 's'}
                  </span>
                </button>
              </div>
            )}
            <div className="divide-y divide-gray-100">
            {displayOrders.map((order) => {
              const items = order.items || [];
              const receivedCount = items.filter(i => (i.received_quantity ?? 0) >= i.quantity).length;
              const totalItems = items.length;
              const quote = totalItems > 0 && items.some(i => !i.unit_price || i.unit_price <= 0);
              const supplierName = `${order.supplier?.first_name ?? ''} ${order.supplier?.last_name ?? ''}`.trim() || 'Unknown Supplier';
              return (
                <div
                  key={order.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOrderClick(order)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOrderClick(order); } }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors cursor-pointer touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-[15px] font-semibold text-gray-900 truncate">
                        {supplierName}
                      </span>
                      <span className="text-[12px] text-gray-500 truncate flex-shrink-0">· {order.order_number}</span>
                      {order.id && (unreadCounts[order.id] || 0) > 0 && (
                        <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold text-white bg-blue-600 rounded-full">
                          <Mail size={10} />
                          {unreadCounts[order.id]}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-[12px] text-gray-500">{formatDate(order.created_at)}</span>
                      <span className="text-[11px] text-gray-400">{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
                      {totalItems > 0 && order.status !== 'cancelled' && (
                        <span className={`text-[11px] font-medium ${receivedCount === totalItems ? 'text-green-600' : 'text-amber-600'}`}>
                          {receivedCount} of {totalItems} received
                        </span>
                      )}
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${
                        quote ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-600'
                      }`}>
                        {quote ? 'Quote Request' : 'Purchase Order'}
                      </span>
                      {order.project && (
                        <span className="inline-block px-1.5 py-0.5 rounded bg-purple-50 text-[11px] font-medium text-purple-600 truncate max-w-[140px]" title={`Project: ${order.project.title}`}>
                          {order.project.title}
                        </span>
                      )}
                      {order.ready_for_pickup_at && (
                        <span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-medium text-emerald-700 bg-emerald-50">
                          Ready for pickup
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[15px] font-bold text-gray-900">
                      {quote && order.total_amount === 0 ? 'TBD' : formatMoney(order.total_amount)}
                    </div>
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${
                      order.status === 'pending'
                        ? 'bg-orange-50 text-orange-700'
                        : order.status === 'received'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-red-50 text-red-700'
                    }`}>
                      {order.status === 'pending' ? 'Pending' : order.status === 'received' ? 'Received' : 'Cancelled'}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </div>
              );
            })}
            </div>
          </div>
        )}
      </div>

      {!isLoading && activeList.length > 0 && (
        <div className="px-5 py-3.5 bg-gray-50/50 border-t border-gray-200">
          <button
            onClick={handleViewAll}
            className="w-full flex items-center justify-center gap-1 text-sm text-blue-600 font-medium hover:text-blue-700 transition-colors duration-150"
          >
            {activeTab === 'orders' ? 'View All Orders' : 'View All Purchase Orders'}
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </>
  );

  // Nothing pinned to show: behave as if the widget were off.
  if (!showPos && !showOrders) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center text-white/90 hover:text-white transition-all duration-200 ease-out p-2 rounded-xl hover:bg-white/15 active:scale-95 touch-manipulation"
        title="Orders"
        aria-label="Orders"
      >
        <Package className="h-5 w-5 text-white" />

        {showPos && lowStockCount > 0 && (
          <span
            className="absolute -top-1 -left-1 text-white pointer-events-none"
            title={`${lowStockCount} item${lowStockCount === 1 ? '' : 's'} low in stock`}
            aria-label={`${lowStockCount} items low in stock`}
          >
            <AlertCircle className="h-4 w-4" strokeWidth={2} />
          </span>
        )}

        {badgeCount > 0 && (
          <span
            className={`absolute -top-1 -right-1 text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center font-semibold border-2 border-gray-900 ${
              badgeUrgent ? 'bg-red-500' : 'bg-gray-600'
            }`}
          >
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}

        {/* Unread supplier email replies across open orders (PO-specific) */}
        {showPos && totalUnreadReplies > 0 && (
          <span className="absolute -bottom-1 -right-1 bg-blue-500 text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center font-semibold border-2 border-gray-900">
            {totalUnreadReplies > 9 ? '9+' : totalUnreadReplies}
          </span>
        )}
      </button>

      {isOpen && dropdownPosition && createPortal(
        <>
          {/* Backdrop (mobile only) — desktop relies on the outside-click handler */}
          {typeof window !== 'undefined' && window.innerWidth < 640 && (
            <div
              className="fixed inset-0 bg-black/20 z-[9998]"
              onClick={() => setIsOpen(false)}
              style={{ pointerEvents: 'auto' }}
            />
          )}
          <div
            ref={portalContentRef}
            className="fixed bg-white rounded-3xl shadow-2xl border border-gray-200/80 z-[9999] max-h-[600px] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`,
              width: `calc(100vw - 2rem)`,
              maxWidth: '380px',
              pointerEvents: 'auto'
            }}
          >
            {dropdownContent}
          </div>
        </>,
        document.body
      )}

      <CreateOrderModal
        isOpen={isCreateOrderOpen}
        onClose={() => setIsCreateOrderOpen(false)}
        items={stockItems}
        onOrderCreated={async () => {
          await loadOrders();
        }}
      />

      <PlaceOrderModal
        isOpen={isPlaceOrderOpen}
        onClose={() => {
          setIsPlaceOrderOpen(false);
          void loadOrders();
        }}
        items={stockItems}
      />

      {/* PO details — receive items, record bills, cancel/close, etc. */}
      <PurchaseOrderDetailModal
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onOrderUpdated={loadOrders}
      />

      {/* Customer order details — same right-side panel as the Orders tab */}
      {selectedCustOrder && selectedBusiness?.id && (
        <OrderDetailModal
          order={selectedCustOrder}
          businessId={selectedBusiness.id}
          onClose={() => setSelectedCustOrder(null)}
          onChanged={loadOrders}
          onInvoiced={loadOrders}
        />
      )}

      {/* Staff-entered customer order (e.g. phone order) */}
      {isNewCustOrderOpen && selectedBusiness?.id && (
        <NewOrderModal
          businessId={selectedBusiness.id}
          onClose={() => setIsNewCustOrderOpen(false)}
          onCreated={() => {
            setIsNewCustOrderOpen(false);
            loadOrders();
          }}
        />
      )}
    </div>
  );
};
